/**
 * useRecipientSuggestions - autocomplete dropdown data for the reply
 * composer's To / Cc / Bcc fields.
 *
 * Four signal sources, ranked server-side: company > thread > history
 * > directory (see backend recipient_suggestions endpoint). The hook plumbs
 * the call through React Query with a short cache key so toggling
 * between fields doesn't refetch unnecessarily; rows update on the
 * 15s detail-page refetch interval if the agent keeps the composer
 * open.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { RecipientSuggestion } from "@/types/api";

export function recipientSuggestionsQueryKey(
  ticketNumber: number,
  q: string,
): readonly unknown[] {
  // Lower-case the query so "Jon" and "jon" share a cache slot - the
  // backend matches case-insensitively, so duplicating cache rows for
  // the same intent is wasted.
  return ["agent", "tickets", "recipient-suggestions", ticketNumber, q.toLowerCase()] as const;
}

export interface UseRecipientSuggestionsOptions {
  enabled?: boolean;
}

export function useRecipientSuggestions(
  ticketNumber: number,
  q: string,
  options: UseRecipientSuggestionsOptions = {},
): UseQueryResult<RecipientSuggestion[]> {
  return useQuery({
    queryKey: recipientSuggestionsQueryKey(ticketNumber, q),
    enabled: options.enabled !== false,
    // Suggestions are derived from comments + users + watchers; they
    // don't change every second. A 30s stale window prevents a flood
    // of requests as the agent types without making results stale.
    staleTime: 30_000,
    queryFn: async () => {
      const response = await api.get<{ data: RecipientSuggestion[] }>(
        `/agent/tickets/${ticketNumber}/recipient-suggestions`,
        { params: { q } },
      );
      return response.data.data;
    },
  });
}
